import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreditCard, CreditCardFields } from '../CreditCard/CreditCard';
import { FormFooter } from '../FormFooter/FormFooter';
import { BillingAddress, BillingAddressFields } from '../BillingAddress/BillingAddress';
import { creditCardInitialValues, creditCardValidation } from '../CreditCard/CreditCard.meta';
import {
  billingAddressInitialValues,
  billingAddressValidation,
} from '../BillingAddress/BillingAddress.meta';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { useSavePaymentMutation } from '../../../../api/useSavePaymentMutation';
import { routeParamsSelector } from '../../../../../CommonComponents/Router/Selector';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { setIsEditing } from '../../../../state';
import { PaymentOptionsDTO } from '../../../../api/usePaymentQuery';

interface CreditCardPaymentFields extends CreditCardFields, BillingAddressFields {}

export const CreditCardPayment: React.FC = () => {
  const { mutateAsync: savePayment, isLoading, error } = useSavePaymentMutation();

  const { clientId } = useSelector(routeParamsSelector);

  const dispatch = useDispatch();

  const formBag = useForm<CreditCardPaymentFields>({
    defaultValues: {
      ...creditCardInitialValues,
      ...billingAddressInitialValues,
    },

    resolver: yupResolver(creditCardValidation.concat(billingAddressValidation)),
  });

  const onSubmit = useCallback(
    async data => {
      try {
        await savePayment({
          clientId,
          data: { ...data, paymentOption: PaymentOptionsDTO.CreditCard },
        });

        dispatch(setIsEditing(false));
      } catch {
        // Ignore
      }
    },
    [clientId, dispatch, savePayment],
  );

  return (
    <form onSubmit={formBag.handleSubmit(onSubmit)}>
      <ModalBody>
        {error && <Alert message={error.message} variant="error" my="16" />}

        <CreditCard formBag={formBag as any} mb="24" />

        <BillingAddress formBag={formBag as any} />
      </ModalBody>

      <FormFooter isLoading={isLoading} />
    </form>
  );
};
